import { DelayedStreamAdapter } from "../streaming/delayed-stream-adapter";
import {
  isToolResultMessage,
  Message,
} from "../streaming/streaming-message-types";

type DisplayQueueOptions = {
  getMessages: () => Message[];
  setMessages: (next: Message[]) => void;
  emitMessagesUpdated: (messages: Message[]) => void;
  enableAnimations: boolean;
};

export class DisplayQueue {
  private queue: Array<{
    adapter: DelayedStreamAdapter;
    messageIndex: number;
  }> = [];
  private active: {
    adapter: DelayedStreamAdapter;
    messageIndex: number;
  } | null = null;

  constructor(private readonly opts: DisplayQueueOptions) {}

  enqueue(adapter: DelayedStreamAdapter, messageIndex: number): void {
    this.queue.push({ adapter, messageIndex });
    this.pump();
  }

  hasActive(): boolean {
    return this.active !== null;
  }

  hasPending(): boolean {
    return this.queue.length > 0;
  }

  onActiveDisplayComplete(messageIndex: number): void {
    const messages = this.opts.getMessages();
    const newMessages = [...messages];

    const assistantMessage = newMessages[messageIndex];
    if (assistantMessage && assistantMessage.role === "assistant") {
      newMessages[messageIndex] = {
        ...assistantMessage,
        displayState: {
          kind: "displayed",
        },
      } as Message;
    }

    for (let i = 0; i < newMessages.length; i++) {
      const m = newMessages[i];
      if (m && isToolResultMessage(m) && m.displayState?.kind === "hidden") {
        newMessages[i] = {
          ...m,
          displayState: { kind: "displayed" },
        } as Message;
      }
    }

    const nextIndex = newMessages.findIndex(
      (m, idx) =>
        idx > messageIndex &&
        m.role === "assistant" &&
        m.displayState?.kind === "hidden"
    );
    if (nextIndex !== -1) {
      const nextAssistant = newMessages[nextIndex];
      newMessages[nextIndex] = {
        ...nextAssistant,
        ...(this.opts.enableAnimations
          ? {
              displayState: {
                kind: "displaying",
                visibleText: "",
              },
            }
          : {
              displayState: {
                kind: "displayed",
              },
            }),
      } as Message;
    }

    this.opts.setMessages(newMessages);
    this.opts.emitMessagesUpdated([...newMessages]);

    this.active = null;
    this.pump();
  }

  clear(): void {
    this.active?.adapter.abort();
    for (const q of this.queue) q.adapter.abort();
    this.active = null;
    this.queue = [];
  }

  private pump(): void {
    if (this.active || this.queue.length === 0) return;
    const next = this.queue.shift();
    if (!next) return;
    this.active = next;
    this.active.adapter.resumeDisplay();
  }
}
